import React, { useEffect, useState } from 'react';

import { AgencyDto } from '../../model/dto/AgencyDto';
import { CollaboratorDto } from '../../model/dto/CollaboratorDto';
import { isEmptyArray } from 'formik';
import { useDataSource } from 'leeloo-react/component/hook/useDataSource';
import { ResourceDataProviderEnum } from '../../enum/ResourceDataProviderEnum';
import { SelectField } from 'leeloo-react/component/form/field/SelectField';
import { Spinner } from 'react-bootstrap';

/**
 * Un sélecteur d'agence et de collaborateur au sein de cette agence
 * Pour l'instant, il permet d'exporter la liste des collaborateurs sur l'agence choisie
 *   et les collaborateurs sélectionnés
 */
export function AgencySelector({
  setSelectedCollaborateurs, setCollaborators
}: {
  setSelectedCollaborateurs: (collaboratorIds: string[]) => void,
  setCollaborators: (collaborators: CollaboratorDto[]) => void,
} ) {

  // L'agence sélectionnée
  const [agency, setAgency] = useState<string|null>(null);

  // La liste des agences sélectionnables sous forme de datasource
  const dsAgencies = useDataSource<AgencyDto>({
    resource: ResourceDataProviderEnum.agency,
    noLoadAfterInit: true,
  });

  // Pour éviter de recharger les agences en boucle au démarrage de la page
  const [agenciesLoaded, setAgenciesLoaded] = useState<boolean>(false);

  // La liste des collaborateurs pour l'agence sélectionnée
  const dsCollaboratorsByAgency = useDataSource<CollaboratorDto>({
    resource: ResourceDataProviderEnum.userCollaboratorsByAgency,
    noLoadAfterInit: true,
  });

  // Lance le chargement initial de la liste des agences
  useEffect(() => {
    if (!agenciesLoaded) {
      dsAgencies.refreshData();
    }
  });

  // Réceptionne de la liste des agences
  useEffect(() => {
    setAgenciesLoaded(true);
    if (dsAgencies.dataList && dsAgencies.total) {
      getAgency();
    }
  }, [dsAgencies.dataList]);

  // Lors de la sélection d'une agence, recharge la liste des collaborateurs et exporte
  //   l'agence pour réutilisation hors du composant
  useEffect(() => {
    if (agency) {
      dsCollaboratorsByAgency.setParams({ id: agency });
    }
  }, [agency]);

  // Lors de la réception d'une liste de collaborateurs, exporte la liste
  //   pour réutilisation hors du composant
  useEffect(() => {
    if (dsCollaboratorsByAgency.dataList && dsCollaboratorsByAgency.total) {
      setCollaborators(dsCollaboratorsByAgency.dataList );
    }
  }, [dsCollaboratorsByAgency.dataList]);

  /**
   * Convertit la liste des agences reçue en liste de valeurs pour le SelectField
   *   et met à jour l'agence sélectionnée
   */
  const getAgency = () => {
    if (dsAgencies.dataList && !isEmptyArray(dsAgencies.dataList)) {
      setAgency(dsAgencies.dataList[0].id);
    }
  };

  return (
    <>
      { dsAgencies.total === 0 && (
        <>
          <Spinner animation="border" size="sm" className="mr-1" />
          &nbsp;Chargement des apprenants...
        </>
      )}
      { dsAgencies.total > 1 &&
      <>
        <h5 className="label-popup">Agence</h5>
        <SelectField
          isClearable={false}
          value={agency}
          optionsList={dsAgencies.dataList ? dsAgencies.dataList.map((data: any) => ({
            value: data.id,
            label: data.name,
          })) : []}
          onChangeValue={setAgency}
          placeholder=" "
          className="mb-3"
        />
      </>
      }
      { agency && (
        <>
          <h5 className="label-popup">Qui ?</h5>
          <SelectField
              isMulti
              isClearable={false}
              optionsList={dsCollaboratorsByAgency.dataList ? dsCollaboratorsByAgency.dataList.map((collaborator) => ({
                value: collaborator.id,
                label: collaborator.fullname,
              })) : []}
              onChangeValue={setSelectedCollaborateurs}
              placeholder="Sélectionner un ou des apprenant(s)"
              className="mb-3 custom-react-select-container"
          />
        </>
      )}
    </>
  );
}
